<script setup>
  import {computed} from "vue";

  import DeveloperJsonViewer from "@components/_DevOnly/DeveloperJsonViewer.vue";
  import ComponentEditor from "@components/Editor/Editor.vue";
  import {
    MedCard,
    MedCardContent,
    MedCardHeader,
    MedTabContent,
    MedTabList,
    MedTabs,
    MedTabTrigger,
  } from "@pharma4u/medicheck-components";

  defineOptions({
    name: "AiResultCard",
  });

  const props = defineProps({
    result: {
      type: Object,
      required: true,
    },
    header: {
      type: String,
      default: "Result",
    },
  });

  const parsedJson = computed(() => {
    if (!props.result?.response || typeof props.result.response !== "string") return null;
    try {
      return JSON.parse(props.result.response);
    } catch {
      return null;
    }
  });

  const defaultTab = computed(() => (parsedJson.value !== null ? "json" : "editor"));
</script>

<template>
  <MedCard>
    <MedCardHeader class="flex items-center justify-between">
      <span>{{ header }}</span>
      <slot name="header-actions" />
    </MedCardHeader>
    <MedCardContent>
      <slot name="before-tabs" />

      <MedTabs :default-value="defaultTab">
        <MedTabList>
          <MedTabTrigger v-if="parsedJson !== null" value="json">JSON</MedTabTrigger>
          <MedTabTrigger v-else value="editor">Editor</MedTabTrigger>
          <MedTabTrigger value="raw">Raw</MedTabTrigger>
          <MedTabTrigger value="thoughts" :disabled="!result.thoughts">Thoughts</MedTabTrigger>
          <MedTabTrigger value="steps" :disabled="!result.steps || result.steps.length === 0">Steps</MedTabTrigger>
          <MedTabTrigger value="messages" :disabled="!result.messages || result.messages.length === 0">
            Messages
          </MedTabTrigger>
          <MedTabTrigger
            value="technical_terms"
            :disabled="!result.updated_placeholders || Object.keys(result.updated_placeholders).length === 0"
          >
            Technical Terms
          </MedTabTrigger>
          <div class="flex-1" />
          <MedTabTrigger value="debug">Debug Info</MedTabTrigger>
          <MedTabTrigger value="statistics">Statistics</MedTabTrigger>
        </MedTabList>

        <!-- JSON -->
        <MedTabContent v-if="parsedJson !== null" value="json">
          <DeveloperJsonViewer :value="parsedJson" />
        </MedTabContent>

        <!-- Editor -->
        <MedTabContent v-else value="editor">
          <ComponentEditor :model-value="result.response" :disabled="true" />
        </MedTabContent>

        <!-- Raw -->
        <MedTabContent value="raw">
          <pre>{{ result.response }}</pre>
        </MedTabContent>

        <!-- Thoughts -->
        <MedTabContent value="thoughts">
          <pre>{{ result.thoughts ?? "-" }}</pre>
        </MedTabContent>

        <!-- Steps -->
        <MedTabContent value="steps">
          <div v-if="result.steps && result.steps.length > 0" class="space-y-4">
            <div
              v-for="(step, stepIndex) in result.steps"
              :key="stepIndex"
              class="rounded-lg border border-gray-200 bg-white"
            >
              <!-- Step header -->
              <div class="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-4 py-2">
                <span class="rounded bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-800">
                  Step {{ stepIndex + 1 }}
                </span>
                <span
                  v-if="step.finish_reason"
                  class="rounded px-2 py-0.5 text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-700': step.finish_reason === 'stop',
                    'bg-yellow-100 text-yellow-700': step.finish_reason === 'tool_calls',
                    'bg-gray-100 text-gray-600': !['stop', 'tool_calls'].includes(step.finish_reason),
                  }"
                >
                  {{ step.finish_reason }}
                </span>
                <span v-if="step.usage" class="ml-auto text-xs text-gray-500">
                  Tokens: {{ step.usage.prompt_tokens ?? 0 }} in / {{ step.usage.completion_tokens ?? 0 }} out
                  <template v-if="step.usage.reasoning_tokens">/ {{ step.usage.reasoning_tokens }} reasoning</template>
                  <template v-if="step.usage.cache_write_input_tokens || step.usage.cache_read_input_tokens">
                    (cached input: {{ step.usage.cache_write_input_tokens ?? 0 }} written,
                    {{ step.usage.cache_read_input_tokens ?? 0 }} read)
                  </template>
                </span>
              </div>

              <div class="space-y-3 p-4">
                <!-- Text output -->
                <div v-if="step.text">
                  <h5 class="mb-1 text-xs font-semibold text-gray-500 uppercase">Text</h5>
                  <pre class="rounded bg-gray-50 p-3 text-sm">{{ step.text }}</pre>
                </div>

                <!-- Tool Calls -->
                <div v-if="step.tool_calls && step.tool_calls.length > 0">
                  <h5 class="mb-2 text-xs font-semibold text-gray-500 uppercase">
                    Tool Calls ({{ step.tool_calls.length }})
                  </h5>
                  <div class="space-y-2">
                    <div
                      v-for="(toolCall, tcIndex) in step.tool_calls"
                      :key="tcIndex"
                      class="rounded border border-amber-200 bg-amber-50 p-3"
                    >
                      <div class="mb-1 flex items-center gap-2">
                        <span class="font-mono text-sm font-semibold text-amber-900">{{ toolCall.name }}</span>
                        <span class="text-[10px] text-gray-400">ID: {{ toolCall.id }}</span>
                      </div>
                      <div v-if="toolCall.arguments" class="mt-1">
                        <span class="text-[10px] font-medium text-gray-500 uppercase">Arguments</span>
                        <pre class="mt-0.5 rounded bg-white p-2 text-xs">{{
                          typeof toolCall.arguments === "string"
                            ? toolCall.arguments
                            : JSON.stringify(toolCall.arguments, null, 2)
                        }}</pre>
                      </div>
                      <div v-if="toolCall.reasoning_summary" class="mt-2">
                        <span class="text-[10px] font-medium text-gray-500 uppercase">Reasoning Summary</span>
                        <pre class="mt-0.5 rounded bg-white p-2 text-xs">{{ toolCall.reasoning_summary }}</pre>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tool Results -->
                <div v-if="step.tool_results && step.tool_results.length > 0">
                  <h5 class="mb-2 text-xs font-semibold text-gray-500 uppercase">
                    Tool Results ({{ step.tool_results.length }})
                  </h5>
                  <div class="space-y-2">
                    <div
                      v-for="(toolResult, trIndex) in step.tool_results"
                      :key="trIndex"
                      class="rounded border p-3"
                      :class="toolResult.result?.error ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'"
                    >
                      <div class="mb-1 flex items-center gap-2">
                        <span
                          class="font-mono text-sm font-semibold"
                          :class="toolResult.result?.error ? 'text-red-900' : 'text-green-900'"
                        >
                          {{ toolResult.name }}
                        </span>
                        <span class="text-[10px] text-gray-400">ID: {{ toolResult.id }}</span>
                        <span
                          v-if="toolResult.result?.error"
                          class="rounded bg-red-200 px-1.5 py-0.5 text-[10px] font-bold text-red-800"
                        >
                          ERROR
                        </span>
                      </div>
                      <div v-if="toolResult.result" class="mt-1">
                        <span class="text-[10px] font-medium text-gray-500 uppercase">Result</span>
                        <pre class="mt-0.5 max-h-64 overflow-auto rounded bg-white p-2 text-xs">{{
                          typeof toolResult.result === "string"
                            ? toolResult.result
                            : JSON.stringify(toolResult.result, null, 2)
                        }}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="py-8 text-center text-sm text-gray-400">Keine Steps vorhanden.</p>
        </MedTabContent>

        <!-- Messages -->
        <MedTabContent value="messages">
          <div v-if="result.messages && result.messages.length > 0" class="space-y-4">
            <div
              v-for="(message, msgIndex) in result.messages"
              :key="msgIndex"
              class="rounded-lg border border-gray-200 bg-white"
            >
              <!-- Message header -->
              <div class="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-4 py-2">
                <span
                  class="rounded px-2 py-0.5 text-xs font-bold"
                  :class="{
                    'bg-blue-100 text-blue-800': message.role === 'assistant',
                    'bg-amber-100 text-amber-800': message.role === 'tool_result',
                    'bg-green-100 text-green-800': message.role === 'user',
                    'bg-purple-100 text-purple-800': message.role === 'system',
                    'bg-gray-100 text-gray-700': !['assistant', 'tool_result', 'user', 'system'].includes(message.role),
                  }"
                >
                  {{ message.role }}
                </span>
                <span class="text-xs text-gray-400">#{{ msgIndex + 1 }}</span>
              </div>

              <div class="space-y-3 p-4">
                <!-- Content -->
                <div v-if="message.content">
                  <h5 class="mb-1 text-xs font-semibold text-gray-500 uppercase">Content</h5>
                  <pre class="rounded bg-gray-50 p-3 text-sm">{{ message.content }}</pre>
                </div>

                <!-- Tool Calls -->
                <div v-if="message.toolCalls && message.toolCalls.length > 0">
                  <h5 class="mb-2 text-xs font-semibold text-gray-500 uppercase">
                    Tool Calls ({{ message.toolCalls.length }})
                  </h5>
                  <div class="space-y-2">
                    <div
                      v-for="(toolCall, tcIndex) in message.toolCalls"
                      :key="tcIndex"
                      class="rounded border border-amber-200 bg-amber-50 p-3"
                    >
                      <div class="mb-1 flex items-center gap-2">
                        <span class="font-mono text-sm font-semibold text-amber-900">{{ toolCall.name }}</span>
                        <span v-if="toolCall.id" class="text-[10px] text-gray-400">ID: {{ toolCall.id }}</span>
                      </div>
                      <div v-if="toolCall.arguments" class="mt-1">
                        <span class="text-[10px] font-medium text-gray-500 uppercase">Arguments</span>
                        <pre class="mt-0.5 rounded bg-white p-2 text-xs">{{
                          typeof toolCall.arguments === "string"
                            ? toolCall.arguments
                            : JSON.stringify(toolCall.arguments, null, 2)
                        }}</pre>
                      </div>
                      <div v-if="toolCall.reasoning_summary" class="mt-2">
                        <span class="text-[10px] font-medium text-gray-500 uppercase">Reasoning Summary</span>
                        <pre class="mt-0.5 rounded bg-white p-2 text-xs">{{ toolCall.reasoning_summary }}</pre>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tool Results -->
                <div v-if="message.toolResults && message.toolResults.length > 0">
                  <h5 class="mb-2 text-xs font-semibold text-gray-500 uppercase">
                    Tool Results ({{ message.toolResults.length }})
                  </h5>
                  <div class="space-y-2">
                    <div
                      v-for="(toolResult, trIndex) in message.toolResults"
                      :key="trIndex"
                      class="rounded border border-green-200 bg-green-50 p-3"
                    >
                      <div class="mb-1 flex items-center gap-2">
                        <span class="font-mono text-sm font-semibold text-green-900">{{ toolResult.name }}</span>
                        <span v-if="toolResult.id" class="text-[10px] text-gray-400">ID: {{ toolResult.id }}</span>
                      </div>
                      <div v-if="toolResult.result" class="mt-1">
                        <span class="text-[10px] font-medium text-gray-500 uppercase">Result</span>
                        <pre class="mt-0.5 max-h-64 overflow-auto rounded bg-white p-2 text-xs">{{
                          typeof toolResult.result === "string"
                            ? toolResult.result
                            : JSON.stringify(toolResult.result, null, 2)
                        }}</pre>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Empty message hint -->
                <p
                  v-if="
                    !message.content &&
                    (!message.toolCalls || message.toolCalls.length === 0) &&
                    (!message.toolResults || message.toolResults.length === 0)
                  "
                  class="text-sm text-gray-400 italic"
                >
                  (Keine Inhalte)
                </p>
              </div>
            </div>
          </div>
          <p v-else class="py-8 text-center text-sm text-gray-400">Keine Messages vorhanden.</p>
        </MedTabContent>

        <!-- Technical Terms -->
        <MedTabContent value="technical_terms">
          <div v-for="(replacements, placeholderKey) in result.updated_placeholders" :key="placeholderKey" class="mb-4">
            <h4 class="mb-1 text-sm font-semibold">%{{ placeholderKey }}%</h4>
            <table class="w-full rounded border border-gray-200 text-sm">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-3 py-1.5 text-left font-medium">Fachbegriff</th>
                  <th class="px-3 py-1.5 text-left font-medium">Ersetzung</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(replacement, term) in replacements" :key="term" class="border-t border-gray-100">
                  <td class="px-3 py-1.5">{{ term }}</td>
                  <td class="px-3 py-1.5">{{ replacement }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </MedTabContent>

        <!-- Debug Info -->
        <MedTabContent value="debug">
          <DeveloperJsonViewer :value="result" />
        </MedTabContent>

        <!-- Statistics -->
        <MedTabContent value="statistics">
          <DeveloperJsonViewer :value="result.usage" />
        </MedTabContent>
      </MedTabs>
    </MedCardContent>
  </MedCard>
</template>

<style scoped>
  pre {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
</style>
